import { useEffect, useState, type CSSProperties } from 'react';
import type { LiveBackground, RenderedSlide, ScreenTheme, ThemeFont } from '../../shared/api/types';
import { PdfPage } from './PdfPage';
import { isLightPreset, presetUrl } from './backgrounds';
import { localApi, localUrl } from './local';
import { youtubeThumb } from './youtube';
import { SyncedVideo, SyncedYouTube, type PlayerSync } from './Player';
import { CameraFeed } from './sources';

export const presentationFileUrl = (churchId: string, presentationId: string, resourceId: string) =>
  `/api/presentation/churches/${churchId}/presentations/${presentationId}/resources/${resourceId}/file`;

/** Textos largos (Biblia) en letra algo menor para que quepan sin cortarse. */
const lengthClass = (text: string) => (text.length > 260 ? ' slide__text--longer' : text.length > 150 ? ' slide__text--long' : '');

export const THEME_FONT_FAMILY: Record<ThemeFont, string> = {
  clasica: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  elegante: 'Georgia, "Times New Roman", serif',
  fuerte: '"Arial Narrow", "Roboto Condensed", Impact, system-ui, sans-serif',
  moderna: '"Trebuchet MS", "Segoe UI", Verdana, system-ui, sans-serif',
  manuscrita: '"Segoe Script", "Brush Script MT", "Lucida Handwriting", cursive',
};

/** Variables y clases del tema para la caja de la diapositiva. */
function themeProps(theme: ScreenTheme | null | undefined): { style?: CSSProperties; className: string } {
  if (!theme) return { className: '' };
  return {
    style: { '--slide-bg': theme.bgColor, '--slide-color': theme.textColor, '--slide-font': THEME_FONT_FAMILY[theme.font] } as CSSProperties,
    className: `${theme.size !== 'normal' ? ` slide--${theme.size}` : ''}${theme.position && theme.position !== 'centro' ? ` slide--${theme.position}` : ''}${theme.align && theme.align !== 'centro' ? ` slide--${theme.align}` : ''}${theme.uppercase ? ' slide--upper' : ''}${theme.font === 'fuerte' ? ' slide--fuerte' : ''}`,
  };
}

type Logo = { resourceId: string | null; name: string; kind?: 'image' | 'video'; localFile?: string | null };

/**
 * Una diapositiva en proporción 16:9. El texto escala con el ancho de la caja, así sirve igual para
 * la miniatura del operador que para el proyector. Con `theme` toma la letra, colores y el fondo de
 * la iglesia (lo que no trae fondo propio usa el del tema); con `logo` muestra el logo en su lugar.
 */
export function SlideView({
  slide,
  fileUrl,
  blackout = false,
  muted = true,
  label,
  theme,
  logo,
  background,
  player,
  hideText = false,
}: {
  slide: RenderedSlide | null;
  fileUrl: (resourceId: string) => string;
  blackout?: boolean;
  muted?: boolean;
  label?: string;
  theme?: ScreenTheme | null;
  logo?: Logo | null;
  /** Fondo en vivo: tapa el fondo del elemento y del tema detrás de la letra. */
  background?: LiveBackground | null;
  /** Salidas y monitor En vivo: los videos obedecen el comando de reproducción (sin controles). */
  player?: PlayerSync;
  /** «Ocultar letra»: sin texto ni pie, solo el fondo. */
  hideText?: boolean;
}) {
  const isMedia = slide?.kind === 'media';
  const live = background && !isMedia ? <LiveBg background={background} fileUrl={fileUrl} /> : null;
  if (live && !blackout && !(logo && !blackout)) {
    const t0 = themeProps(theme);
    return (
      <div className={`slide${slide?.caption ? ' slide--captioned' : ''}${t0.className}`} style={t0.style} role="img" aria-label={label ?? (slide ? `${slide.title}${slide.text ? `: ${slide.text}` : ''}` : 'Fondo')}>
        {live}
        {slide?.text && !hideText ? <p className={`slide__text slide__text--on-bg${lengthClass(slide.text)}`}>{textOf(slide)}</p> : null}
        {slide?.caption && !hideText ? <p className="slide__caption">{slide.caption}</p> : null}
      </div>
    );
  }
  // Fondo: la imagen del elemento, su fondo KEORA, la imagen del tema o el fondo KEORA del tema.
  const own = slide?.resource ?? null;
  const preset = slide && !isMedia && !own ? (slide.backgroundPreset ?? (theme?.background ? null : (theme?.backgroundPreset ?? null))) : null;
  const resource = own ?? (slide && !isMedia && !preset ? (theme?.background ?? null) : null);
  const t = themeProps(theme);
  if (logo && !blackout) {
    return (
      <div className={`slide slide--logo${t.className}`} style={t.style} role="img" aria-label={label ?? `Logo: ${logo.name}`}>
        <LogoMedia logo={logo} fileUrl={fileUrl} />
      </div>
    );
  }
  return (
    <div className={`slide${slide?.caption && !blackout ? ' slide--captioned' : ''}${blackout ? ' slide--black' : ''}${isLightPreset(preset) ? ' slide--light' : ''}${t.className}`} style={t.style} role="img" aria-label={label ?? (slide ? `${slide.title}${slide.text ? `: ${slide.text}` : ''}` : 'Sin diapositiva')}>
      {slide && !blackout ? (
        <>
          {preset ? <img className="slide__bg" src={presetUrl(preset)} alt="" /> : null}
          {slide.localFile ? <LocalMedia file={slide.localFile} title={slide.title} muted={muted} player={player} /> : null}
          {slide.youtube ? player ? <SyncedYouTube id={slide.youtube} title={slide.title} sync={player} /> : <YouTubeMedia id={slide.youtube} title={slide.title} /> : null}
          {slide.camera ? <CameraFeed sourceId={slide.camera} className="slide__media slide__camera" withAudio={!muted} /> : null}
          {resource?.kind === 'image' ? <img className={isMedia ? 'slide__media' : 'slide__bg'} src={fileUrl(resource.id)} alt="" /> : null}
          {resource?.kind === 'video' ? player ? <SyncedVideo className="slide__media" src={fileUrl(resource.id)} sync={player} /> : <video className="slide__media" src={fileUrl(resource.id)} autoPlay muted={muted} playsInline loop={false} /> : null}
          {resource?.kind === 'document' && isMedia ? <PdfPage className="slide__media" url={fileUrl(resource.id)} page={slide.page ?? 1} /> : null}
          {resource?.kind === 'audio' && isMedia ? (
            <>
              <p className="slide__text">♪ {slide.title}</p>
              {/* Solo suena en la pantalla principal (las miniaturas van silenciadas). */}
              {!muted ? <audio src={fileUrl(resource.id)} autoPlay /> : null}
            </>
          ) : null}
          {slide.text && !hideText ? <p className={`slide__text${(resource || preset) && !isMedia ? ' slide__text--on-bg' : ''}${lengthClass(slide.text)}`}>{textOf(slide)}</p> : null}
          {slide.caption && !hideText ? <p className="slide__caption">{slide.caption}</p> : null}
        </>
      ) : !blackout && theme?.background ? (
        // Sin nada al aire queda el fondo del tema (como «Ocultar letra»: quita el texto, deja el fondo).
        <img className="slide__bg" src={fileUrl(theme.background.id)} alt="" />
      ) : !blackout && theme?.backgroundPreset ? (
        <img className="slide__bg" src={presetUrl(theme.backgroundPreset)} alt="" />
      ) : null}
    </div>
  );
}

/** Texto de la diapositiva; en la Biblia, el número del versículo va chiquito arriba (como EasyWorship). */
function textOf(slide: RenderedSlide) {
  const m = slide.kind === 'scripture' && slide.text ? /^(\d{1,3}) ([\s\S]*)$/.exec(slide.text) : null;
  if (!m) return slide.text;
  return (
    <>
      <sup className="slide__verse">{m[1]}</sup> {m[2]}
    </>
  );
}

/** Video (en bucle, sin sonido) o imagen detrás de la letra. */
function LiveBg({ background: b, fileUrl }: { background: LiveBackground; fileUrl: (resourceId: string) => string }) {
  if (b.camera) return <CameraFeed sourceId={b.camera} className="slide__bg" />;
  const src = b.resource ? fileUrl(b.resource.id) : b.localFile && localApi() ? localUrl(b.localFile.name) : null;
  const kind = b.resource?.kind ?? b.localFile?.kind;
  if (!src) return null;
  if (kind === 'video') return <video className="slide__bg" src={src} autoPlay muted loop playsInline />;
  return <img className="slide__bg" src={src} alt="" />;
}

/**
 * Conteo a pantalla completa con el tema de la iglesia. Al llegar a cero muestra el logo, así la
 * pantalla nunca se queda en «0:00».
 */
export function CountdownView({ label, time, theme, logo, fileUrl }: { label: string | null; time: string; theme?: ScreenTheme | null; logo: Logo; fileUrl: (resourceId: string) => string }) {
  if (time === '0:00') return <SlideView slide={null} fileUrl={fileUrl} theme={theme} logo={logo} />;
  const t = themeProps(theme);
  return (
    <div className={`slide slide--countdown${t.className}`} style={t.style} role="timer" aria-label={`${label ?? 'Faltan'} ${time}`}>
      {theme?.background ? <img className="slide__bg" src={fileUrl(theme.background.id)} alt="" /> : theme?.backgroundPreset ? <img className="slide__bg" src={presetUrl(theme.backgroundPreset)} alt="" /> : null}
      <div className="slide__countdown-box">
        {label ? <p className="slide__countdown-label">{label}</p> : null}
        <p className="slide__countdown">{time}</p>
      </div>
    </div>
  );
}

/** Minutos y segundos que faltan, corregidos con la hora del servidor. */
export function useCountdown(endsAt: string | null, serverTime: string | null): string | null {
  const [now, setNow] = useState(() => Date.now());
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (serverTime) setOffset(new Date(serverTime).getTime() - Date.now());
  }, [serverTime]);
  useEffect(() => {
    if (!endsAt) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [endsAt]);
  if (!endsAt) return null;
  const left = Math.max(0, Math.round((new Date(endsAt).getTime() - (now + offset)) / 1000));
  return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
}

/**
 * Video de YouTube en una miniatura (Preview, «Sigue»): solo su imagen. En las salidas y en el
 * monitor En vivo se reproduce de verdad, sincronizado (SyncedYouTube).
 */
function YouTubeMedia({ id, title }: { id: string; title: string }) {
  return (
    <>
      <img className="slide__media" src={youtubeThumb(id)} alt="" />
      <p className="slide__caption">▶ YouTube · {title}</p>
    </>
  );
}

/**
 * Archivo de la computadora de proyección: la app KEORA Multimedia lo muestra desde su carpeta; en
 * cualquier otra pantalla (celular, OBS, otra computadora) solo se avisa qué es.
 */
function LocalMedia({ file, title, muted, player }: { file: { name: string; kind: 'image' | 'video' | 'audio' }; title: string; muted: boolean; player?: PlayerSync | undefined }) {
  if (!localApi()) return <p className="slide__text slide__text--small">📁 {file.name}</p>;
  const src = localUrl(file.name);
  if (file.kind === 'image') return <img className="slide__media" src={src} alt="" />;
  if (file.kind === 'video') return player ? <SyncedVideo className="slide__media" src={src} sync={player} /> : <video className="slide__media" src={src} autoPlay muted={muted} playsInline loop={false} />;
  return (
    <>
      <p className="slide__text">♪ {title}</p>
      {!muted ? <audio src={src} autoPlay /> : null}
    </>
  );
}

/** El logo: imagen, o video en bucle sin sonido. El de la computadora solo se ve dentro de la app. */
function LogoMedia({ logo, fileUrl }: { logo: Logo; fileUrl: (resourceId: string) => string }) {
  const src = logo.localFile ? (localApi() ? localUrl(logo.localFile) : null) : logo.resourceId ? fileUrl(logo.resourceId) : null;
  if (!src) return <p className="slide__text">{logo.name}</p>;
  if (logo.kind === 'video') return <video className="slide__logo" src={src} autoPlay muted loop playsInline />;
  return <img className="slide__logo" src={src} alt="" />;
}
